import React, { useState, forwardRef, useCallback, useRef, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import {
  isCartEmpty,
  getCartLineItems,
  getCartLineItemsCount,
  getCartSubtotalPriceAmount,
  getCartDiscount,
  getCartLineItemsSubtotalPrice,
  isCartBusy,
  getCartError,
  clearCartError
} from '../../redux/slices/cart'
import { toggleCartDialog, isCartDialogOpen } from '../../redux/slices/layout'
import {
  getCartEmptyCopy,
  getCartCouponCopy,
  getCartTitle
} from '../../redux/slices/content'
import LineItem from './LineItem'
import RichText from '../RichText'
import ErrorPopup from '../ErrorPopup'
import { formatPrice } from '../../helpers/format'
import useOutsideClickListener from '../../hooks/useOutsideClickListener'
import CartFooter from './CartFooter'
import theme from '../../style/theme'
import noise from '../Noise/noise.png'
import { useHoverCursor } from '../Cursor'
import { syncShopifyCart } from '../../redux/api'
import FreeShippingProgress from './FreeShippingProgress'

const CartDrawer = forwardRef((props, ref) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const open = useSelector(isCartDialogOpen)
  const emptyCartCopy = useSelector(getCartEmptyCopy)
  const couponCopy = useSelector(getCartCouponCopy)
  const title = useSelector(getCartTitle)
  const lineItems = useSelector(getCartLineItems)
  const lineItemCount = useSelector(getCartLineItemsCount)
  const totalAmount = useSelector(getCartSubtotalPriceAmount)
  const lineItemsSubtotalPrice = useSelector(getCartLineItemsSubtotalPrice)
  const discount = useSelector(getCartDiscount)
  const empty = useSelector(isCartEmpty)
  const busy = useSelector(isCartBusy)
  const cartError = useSelector(getCartError)

  const [postcodeValid, setPostcodeValid] = useState(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isManualLoading, setIsManualLoading] = useState(false)
  const [localError, setLocalError] = useState(null)

  // Handle cart errors from Redux
  useEffect(() => {
    if (cartError) {
      setLocalError(cartError)
    }
  }, [cartError])

  // Clear errors when closing drawer
  useEffect(() => {
    if (!open && localError) {
      setLocalError(null)
      // Also clear any error in Redux state
      if (typeof clearCartError === 'function') {
        dispatch(clearCartError())
      }
    }
  }, [open, localError, dispatch])

  // Sync with Shopify cart when drawer opens
  useEffect(() => {
    if (open) {
      setIsManualLoading(true)
      // Also directly call the sync function to ensure immediate update
      syncShopifyCart(dispatch, () => ({}))
        .then(() => {
          setTimeout(() => setIsManualLoading(false), 500) // Add slight delay for better user experience
        })
        .catch((error) => {
          setIsManualLoading(false)
          setLocalError(error || { message: 'Failed to sync with your cart' })
        })
    }
  }, [open, dispatch])

  // Overall loading state
  const isLoading = isManualLoading || busy

  const handleClose = useCallback(() => {
    if (!isAnimating && !isLoading) {
      setIsAnimating(true)
      // Add a small delay before actually closing
      setTimeout(() => {
        dispatch(toggleCartDialog())
        setIsAnimating(false)
      }, 100)
    }
  }, [dispatch, isAnimating, isLoading])

  // Close the drawer when clicking outside
  const containerRef = useOutsideClickListener(open, handleClose)

  const closeButtonRef = useRef()
  useHoverCursor(closeButtonRef)

  // Clean up overflow style when component unmounts
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden' // Prevent body scrolling
    }

    return () => {
      document.body.style.overflow = '' // Re-enable body scrolling
    }
  }, [open])

  // Handle escape key press
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && open && !isLoading) {
        handleClose()
      }
    }

    window.addEventListener('keydown', handleEscKey)
    return () => {
      window.removeEventListener('keydown', handleEscKey)
    }
  }, [open, handleClose, isLoading])

  // Function to clear error
  const handleClearError = () => {
    setLocalError(null)
    // Also clear any error in Redux state if needed
    if (typeof clearCartError === 'function') {
      dispatch(clearCartError())
    }
  }

  return (
    <div className={cn(classes.drawerWrapper, { open })}>
      {/* Overlay */}
      <div
        className={classes.overlay}
        onClick={handleClose}
      />

      {/* Drawer */}
      <aside className={classes.drawer}>
        <div className={classes.container} ref={containerRef}>
          <div className={classes.cartMain}>
            <header className={classes.header}>
              <h4 className={classes.title}>
                {title}
                {lineItemCount > 0 && (
                  <div className={cn(classes.lineItemCount, { small: lineItemCount >= 10 })}>
                    <span className='count'>{lineItemCount}</span>
                  </div>
                )}
              </h4>
              <div className={classes.headerControls}>
                <button
                  className={classes.closeButton}
                  aria-label='close'
                  onClick={handleClose}
                  ref={closeButtonRef}
                  disabled={isLoading}
                >
                  <span>
                    <svg width='25px' height='25px' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path fillRule='evenodd' clipRule='evenodd' d='M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z' fill='#0F1729' />
                    </svg>

                  </span>
                </button>
              </div>
            </header>

            {/* Free Shipping Progress Bar */}
            {!empty && !isLoading && (
              <div className={classes.freeShippingWrapper}>
                <FreeShippingProgress totalAmount={totalAmount} />
              </div>
            )}

            {isLoading && (
              <div className={classes.loading}>
                <div className={classes.loader}> </div>
                <p>Updating cart...</p>
              </div>
            )}

            {!isLoading && !empty && (
              <>
                <div className={classes.products}>
                  {lineItems && lineItems.map(lineItem => (
                    <LineItem
                      key={lineItem.id || `${lineItem.variant?.id}-${lineItem.quantity}`}
                      lineItem={lineItem}
                    />
                  ))}
                </div>
                {couponCopy && !isEmpty(couponCopy.text) && (
                  <div className={classes.coupon}>
                    <RichText content={couponCopy.text} />
                  </div>
                )}
                <div className={classes.cartTotals}>
                  {discount ? (
                    <>
                      <div className={cn(classes.totalsFigure, classes.subtotal)}>
                        <span className={classes.figureLabel}>Subtotal</span>
                        <span className={classes.price}>{formatPrice(lineItemsSubtotalPrice)}</span>
                      </div>
                      <div className={cn(classes.totalsFigure, classes.discount)}>
                        <span className={classes.figureLabel}>Discount</span>
                        <span className={classes.price}>{formatPrice(discount)}</span>
                      </div>
                      <div className={cn(classes.totalsFigure, classes.total)}>
                        <span className={classes.figureLabel}>Total</span>
                        <span className={classes.price}>{formatPrice(totalAmount)}</span>
                      </div>
                    </>
                  ) : (
                    <div className={cn(classes.totalsFigure, classes.total)}>
                      <span className={classes.figureLabel}>Total</span>
                      <span className={classes.price}>{formatPrice(totalAmount)}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {!isLoading && empty && (
              <div className={classes.empty}>
                {emptyCartCopy && !isEmpty(emptyCartCopy.text) ? (
                  <RichText content={emptyCartCopy.text} />
                ) : (
                  <span>Your cart is empty!</span>
                )}
              </div>
            )}
          </div>

          <div className={classes.cartFooter}>
            <CartFooter
              cartIsEmpty={empty}
              postcodeValid={postcodeValid}
              setPostcodeValid={setPostcodeValid}
              isLoading={isLoading}
            />
          </div>
        </div>
      </aside>

      {/* Error Popup */}
      {localError && (
        <ErrorPopup
          error={localError}
          onClose={handleClearError}
          autoCloseTime={15000}
        />
      )}
    </div>
  )
})

const useStyles = createUseStyles({
  drawerWrapper: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '100%',
    height: '100%',
    zIndex: 99999999,
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'visibility 0.3s ease',

    '&.open': {
      visibility: 'visible',
      pointerEvents: 'auto'
    }
  },

  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    opacity: 0,
    transition: 'opacity 0.3s ease',

    '.open &': {
      opacity: 1
    }
  },

  drawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    maxWidth: '450px', // Fixed max width for better consistency
    zIndex: 1,
    background: 'white',
    borderLeft: [1, 'solid', theme.colors.primary],
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transform: 'translateX(100%)',
    transition: 'transform 0.3s ease',
    boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.1)',

    '.open &': {
      transform: 'translateX(0)'
    },

    [theme.breakpoints.up('md')]: {
      width: '450px'
    },

    '&:after': {
      content: '',
      width: 'calc(100% + 20rem)',
      height: 'calc(100% + 20rem)',
      backgroundPosition: '50%',
      position: 'absolute',
      left: '-10rem',
      top: '-10rem',
      backgroundImage: `url(${noise})`,
      animation: '$noise 1s steps(2) infinite',
      opacity: 0.6,
      zIndex: -1
    }
  },

  '@keyframes noise': {
    ...theme.noiseAnimation
  },

  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: '20px',
    color: theme.colors.primary,
    overflow: 'hidden'
  },

  cartMain: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },

  title: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    marginBottom: 0,
    fontSize: '24px', // Ensure title is not too large
    fontWeight: 500,
    color: theme.colors.primary
  },

  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },

  refreshButton: {
    background: 'transparent',
    border: 'none',
    color: theme.colors.primary,
    cursor: 'pointer',
    padding: '4px',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
    '&:hover': {
      opacity: 0.7,
      transform: 'rotate(30deg)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      transform: 'none'
    }
  },

  closeButton: {
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: theme.colors.primary,
    background: 'transparent',
    border: 'none',
    padding: '4px',
    marginBottom: 0,
    transition: 'opacity 0.2s ease',
    '&:hover': {
      opacity: 0.7
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },

  lineItemCount: {
    marginLeft: '10px',
    marginBottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.colors.white,
    backgroundColor: theme.colors.primary,
    height: '26px',
    width: '26px',
    borderRadius: '50%',
    fontSize: '16px',

    '&.small': {
      fontSize: '12px'
    },

    '& span': {
      fontFamily: theme.fonts.body,
      fontWeight: theme.fonts.bodyFontWeight,
      lineHeight: 1,
      paddingBottom: '0.2em'
    }
  },

  freeShippingWrapper: {
    marginBottom: '20px',
    width: '100%'
  },

  products: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    borderBottom: [1, 'solid', theme.colors.border],
    marginBottom: '20px',
    paddingBottom: '20px',
    // Increase max-height to allow more products without scrolling
    maxHeight: 'calc(100vh - 100px)'
  },

  coupon: {
    marginBottom: '20px',
    padding: '15px',
    border: [1, 'solid', theme.colors.border],
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    lineHeight: 1.4,
    fontSize: '14px'
  },

  empty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontSize: '16px'
  },

  cartTotals: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingTop: '20px',
    marginBottom: '20px'
  },

  totalsFigure: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  figureLabel: {
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    fontSize: '16px',
    marginRight: '8px'
  },

  price: {
    display: 'inline-block',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    fontSize: '18px'
  },

  total: {
    '& $figureLabel': {
      fontSize: '18px',
      fontWeight: 'bold'
    },
    '& $price': {
      fontSize: '22px',
      fontWeight: 'bold'
    }
  },

  loading: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontSize: '16px'
  },

  loader: {
    border: '4px solid rgba(56, 82, 79, 0.1)',
    borderRadius: '50%',
    borderTop: '4px solid #38524F',
    width: '40px',
    height: '40px',
    animation: '$spin 1s linear infinite',
    marginBottom: '20px'
  },

  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  },

  cartFooter: {
    opacity: 1,
    transform: 'translateY(0)',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    marginTop: 'auto',

    '.drawerWrapper:not(.open) &': {
      opacity: 0,
      transform: 'translateY(100%)'
    }
  }
}, { name: 'CartDrawer' })

export default CartDrawer
